import React, { useEffect, useState } from "react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useNavigate } from "react-router-dom";
import { permissionCheckerService } from "../../services/permission.checker.service";
import {
  faUsers,
  faTruck,
  faUserShield,
  faThLarge,
  faClipboardList,
  faTachometerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import DashboardChart from "../../components/admin/DashboardChart";
import {
  adminDashboardService,
  DashboardData,
} from "../../services/admin.dashboard.service";
import buttonStyles from "../../styles/components/Button.module.css";
import "../../styles/app/Dashboard.css";

const AdminDashboard: React.FC = () => {
  const { adminData } = useAdminAuth();
  const navigate = useNavigate();
  const [statsData, setStatsData] = useState<DashboardData | null>(null);

  // Default dates: Monday of this week to today
  const getDefaultStartDate = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split("T")[0];
  };
  const getDefaultEndDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());

  const fetchStats = async (start?: string, end?: string) => {
    try {
      const response = await adminDashboardService.getDashboardData(start, end);
      if (response.statusCode === 200) {
        setStatsData(response.data);
      }
    } catch (error) {
      console.error("Error loading dashboard stats:", error);
    }
  };

  useEffect(() => {
    fetchStats(getDefaultStartDate(), getDefaultEndDate());
  }, []);

  const stats = [
    {
      title: "Usuarios",
      value: statsData?.userCount.toString() || "0",
      icon: faUsers,
      path: "/admin/users",
      module: "USERS",
      action: "READ",
    },
    {
      title: "Couriers",
      value: statsData?.carrierCount.toString() || "0",
      icon: faTruck,
    },
    {
      title: "Servicios",
      value: statsData?.carrierServiceCount.toString() || "0",
      icon: faClipboardList,
      path: "/admin/courier-services",
      module: "CARRIERSERVICES",
      action: "READ",
    },
    {
      title: "Roles",
      value: statsData?.roleCount.toString() || "0",
      icon: faUserShield,
      path: "/admin/roles",
      module: "ROLES",
      action: "READ",
    },
    {
      title: "Módulos",
      value: statsData?.moduleCount.toString() || "0",
      icon: faThLarge,
    },
    {
      title: "Clientes",
      value: statsData?.customerCount.toString() || "0",
      icon: faUsers,
      path: "/admin/customers",
      module: "CUSTOMERS",
      action: "READ",
    },
    {
      title: "Órdenes",
      value: statsData?.orderCount?.toString() || "0",
      icon: faClipboardList,
      path: `/admin/orders?startDate=${startDate}&endDate=${endDate}`,
      module: "ORDERS",
      action: "READ",
    },
  ];

  const getGreetingName = () => {
    if (adminData?.FirstName && adminData?.LastName)
      return adminData.FirstName + " " + adminData.LastName;
    return "Administrador";
  };

  return (
    <AdminDashboardLayout>
      <DashboardCard
        icon={faTachometerAlt}
        title={`¡Bienvenido, ${getGreetingName()}!`}
        description="Aquí tienes un resumen de la actividad del sistema."
      />

      <div className="stats-grid">
        {stats.map((stat, index) => {
          const hasPermission = stat.path
            ? !stat.module ||
              !stat.action ||
              permissionCheckerService.checkPermission(
                adminData,
                stat.module,
                stat.action
              )
            : false;

          return (
            <div
              key={index}
              className={`stat-card ${hasPermission ? "clickable-card" : ""}`}
              onClick={() => hasPermission && navigate(stat.path!)}
              style={{ cursor: hasPermission ? "pointer" : "default" }}
            >
              <div className="stat-card-info">
                <p className="stat-title">{stat.title}</p>
                <p className="stat-value">{stat.value}</p>
              </div>
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={stat.icon} className="stat-icon" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Date Filters */}
      <div className="dashboard-filters-container">
        <div className="filter-group">
          <label htmlFor="startDate">Fecha Inicio</label>
          <input
            type="date"
            id="startDate"
            className="filter-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="endDate">Fecha Fin</label>
          <input
            type="date"
            id="endDate"
            className="filter-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button
          className={buttonStyles.primary}
          onClick={() => fetchStats(startDate, endDate)}
        >
          Filtrar
        </button>
      </div>

      {/* Custom Bar Chart */}
      <DashboardChart data={statsData?.ordersByDate || []} />
    </AdminDashboardLayout>
  );
};

export default AdminDashboard;

